import { useEffect, useState } from 'react';
import { getCurrentTenant, registerTenantReset } from './tenantScope';

/**
 * createTenantCache — module-level fetch cache + in-flight dedupe +
 * subscribers, SCOPED TO THE SIGNED-IN TENANT (IDI-304).
 *
 * Shared by useCompanyProfile and useBranding, which were two hand-copied
 * versions of the same unkeyed cache. Guarantees:
 *   - an entry filled for tenant A is never served while tenant B is current
 *     (checked on every read, not only on reset);
 *   - a fetch that STARTED under tenant A and resolves after the switch to B
 *     is discarded, not written into B's cache (logout mid-request race);
 *   - resetTenantScopedCaches() (logout / tenant change) clears it and pushes
 *     the empty value to mounted subscribers so the chrome blanks instead of
 *     keeping the previous dealer's name/logo on screen.
 *
 * Failure semantics are unchanged from the originals: a failed fetch yields
 * `{}` for rendering but is not treated as a cached success.
 */
export default function createTenantCache(fetcher, { label = 'cache' } = {}) {
  let entry = null; // { tenant, value }
  let inflight = null; // { tenant, promise }
  const subscribers = new Set();

  const current = () => {
    const t = getCurrentTenant();
    return entry && entry.tenant === t ? entry.value : null;
  };
  const notify = () => {
    const v = current();
    subscribers.forEach((cb) => cb(v));
  };

  function load(force = false) {
    const tenant = getCurrentTenant();
    // Signed out: nothing to fetch, and an unauthenticated request here would
    // 401 right after logout. Callers render the empty fallback.
    if (!tenant) return Promise.resolve({});
    const hit = current();
    if (hit && !force) return Promise.resolve(hit);
    if (inflight && inflight.tenant === tenant) return inflight.promise;

    const promise = Promise.resolve()
      .then(fetcher)
      .then(
        (res) => ({ ok: true, value: res || {} }),
        (err) => {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`[${label}] failed to load:`, err?.message);
          }
          return { ok: false };
        }
      )
      .then(({ ok, value }) => {
        if (inflight && inflight.promise === promise) inflight = null;
        // Tenant changed while we were fetching: this answer belongs to the
        // previous dealer. Drop it.
        if (getCurrentTenant() !== tenant) return current() || {};
        if (ok) entry = { tenant, value };
        else if (!current()) entry = { tenant, value: {}, failed: true };
        notify();
        return current() || {};
      });
    inflight = { tenant, promise };
    return promise;
  }

  function reset() {
    entry = null;
    inflight = null;
    notify();
  }
  registerTenantReset(reset);

  function useCached() {
    const [value, setValue] = useState(current);
    const [loading, setLoading] = useState(() => !current());

    useEffect(() => {
      let active = true;
      const sub = (v) => {
        if (!active) return;
        setValue(v);
        if (!v && getCurrentTenant()) {
          // Reset on tenant switch: refetch for the new tenant.
          setLoading(true);
          load().then(() => active && setLoading(false));
        }
      };
      subscribers.add(sub);
      if (!current()) {
        load().then(() => active && setLoading(false));
      } else {
        setValue(current());
        setLoading(false);
      }
      return () => {
        active = false;
        subscribers.delete(sub);
      };
    }, []);

    return { value, loading };
  }

  return {
    load,
    refresh: () => load(true),
    reset,
    useCached,
    /** Test-only. */
    __clearSubscribers: () => subscribers.clear(),
  };
}
